import { type ReactNode } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import {
  Calendar, CalendarDays, FileCheck, LogOut, PanelLeftClose, PanelLeftOpen,
  Stethoscope, User, Users,
} from 'lucide-react';
import { logout } from '../lib/auth';
import {
  collapseAppRail,
  expandAppRail,
  useAppRailCollapsed,
} from '../store/appShellStore';

/**
 * The one list of app nav items. Both rail widths render from it — there is no
 * second copy anywhere, including in the consultation room, which only asks the
 * app-shell store to switch this component into its collapsed mode (see
 * features/consultation-room/layout/AppRailCollapsed.tsx).
 */
const NAV = [
  { to: '/dashboard', label: 'Dashboard',    Icon: CalendarDays },
  { to: '/patients',  label: 'Patients',     Icon: Users },
  { to: '/consultations/today', label: 'Consultations', Icon: Stethoscope },
  { to: '/schedule',  label: 'Schedule',     Icon: Calendar },
  { to: '/content-review', label: 'Content review', Icon: FileCheck },
  { to: '/profile',   label: 'Profile',      Icon: User },
];

export function Layout({ children, doctorName }: { children: ReactNode; doctorName?: string }) {
  const navigate = useNavigate();
  const collapsed = useAppRailCollapsed();

  function handleLogout() {
    void logout().finally(() => navigate('/login'));
  }

  return (
    <div className="flex min-h-screen bg-ivory fixed inset-0">
      {/* Sidebar. Brand forest green in both widths — this is app chrome, not
          clinical-room surface, so the room's clinical palette stops at its edge. */}
      <aside
        className={`${collapsed ? 'w-[60px]' : 'w-56'} shrink-0 bg-forest text-ivory flex flex-col transition-[width] duration-200 ease-in-out`}
      >
        {collapsed ? (
          <Link
            to="/dashboard"
            title="Kyros Doctor Portal"
            className="flex items-center justify-center py-4 border-b border-white/10"
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-white/10 font-display text-caption font-semibold text-ivory">
              K
            </span>
          </Link>
        ) : (
          <Link to="/dashboard" className="px-6 py-6 border-b border-white/10">
            <p className="font-display text-h3 font-medium text-ivory">Kyros</p>
            <p className="font-body text-caption text-ivory/60 mt-0.5">Doctor Portal</p>
          </Link>
        )}

        {collapsed ? (
          <button
            onClick={expandAppRail}
            title="Expand menu"
            aria-label="Expand menu"
            aria-expanded={false}
            className="mt-3 mb-1 mx-auto flex h-7 w-8 items-center justify-center rounded-md border border-white/15 text-ivory/60 hover:text-ivory transition-colors"
          >
            <PanelLeftOpen size={14} strokeWidth={1.75} />
          </button>
        ) : (
          <button
            onClick={collapseAppRail}
            title="Collapse menu"
            aria-label="Collapse menu"
            aria-expanded={true}
            className="mt-3 mb-1 flex items-center gap-3 px-6 py-1.5 text-ivory/60 hover:text-ivory transition-colors"
          >
            <PanelLeftClose size={16} strokeWidth={1.75} />
            <span className="font-body text-caption">Collapse menu</span>
          </button>
        )}

        <nav className={collapsed ? 'flex-1 py-2 flex flex-col items-center gap-1' : 'flex-1 py-4'}>
          {NAV.map(({ to, label, Icon }) => (
            <NavLink
              key={to}
              to={to}
              title={collapsed ? label : undefined}
              className={({ isActive }: { isActive: boolean }) =>
                collapsed
                  ? `flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${
                      isActive
                        ? 'bg-white/10 text-ivory'
                        : 'text-ivory/70 hover:bg-white/5 hover:text-ivory'
                    }`
                  : `flex items-center gap-3 px-6 py-3 font-body text-body transition-colors ${
                      isActive
                        ? 'bg-white/10 text-ivory'
                        : 'text-ivory/70 hover:bg-white/5 hover:text-ivory'
                    }`
              }
            >
              <Icon size={16} strokeWidth={1.75} />
              {!collapsed && label}
            </NavLink>
          ))}
        </nav>

        {collapsed ? (
          <div className="py-4 border-t border-white/10 flex justify-center">
            <button
              onClick={handleLogout}
              title={doctorName ? `Sign out (${doctorName})` : 'Sign out'}
              aria-label="Sign out"
              className="flex h-9 w-9 items-center justify-center rounded-lg text-ivory/70 hover:bg-white/5 hover:text-ivory transition-colors"
            >
              <LogOut size={16} />
            </button>
          </div>
        ) : (
          <div className="px-6 py-5 border-t border-white/10">
            {doctorName && (
              <p className="font-body text-caption text-ivory/60 mb-3 truncate">{doctorName}</p>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 font-body text-caption text-ivory/70 hover:text-ivory transition-colors"
            >
              <LogOut size={14} />
              Sign out
            </button>
          </div>
        )}
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        {children}
      </main>
    </div>
  );
}
